import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  ArrowLeft, 
  CheckCircle, 
  Ban, 
  Activity, 
  AlertTriangle,
  Save,
  RefreshCw,
  Server,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ClientTRNManagement from './ClientTRNManagement';

interface ClientDetailsProps {
  clientId: string;
  onBack: () => void;
}

const usageData = [
  { month: 'May', invoices: 1123, apiCalls: 41234, success: 40234, failed: 1000 },
  { month: 'Jun', invoices: 1186, apiCalls: 43567, success: 42456, failed: 1111 },
  { month: 'Jul', invoices: 1205, apiCalls: 44321, success: 43245, failed: 1076 },
  { month: 'Aug', invoices: 1247, apiCalls: 45632, success: 44956, failed: 676 },
];

const invoiceTypeData = [
  { type: 'Sales', count: 753, color: '#0052CC' },
  { type: 'Purchase', count: 445, color: '#36B37E' },
  { type: 'Credit Notes', count: 35, color: '#FFAB00' },
  { type: 'Debit Notes', count: 14, color: '#FF5630' }
];

const mockClient = {
  id: 'CLT-001',
  name: 'ABC Trading LLC',
  email: 'contact@abctrading.ae',
  phone: '+971 4 123 4567',
  trn: 'AE123456789',
  address: 'Office 123, Business Bay, United Arab Emirates',
  erp: 'SAP',
  erpVersion: 'S/4HANA',
  status: 'active',
  subscription: 'Premium',
  expiryDate: '2025-12-31',
  apiUrl: 'https://api.abctrading.ae/erp',
  apiKey: 'ais_abc_key_xyz123',
  monthlyInvoices: 1247,
  apiHits: 45632,
  successRate: 98.5,
  failureRate: 1.5,
  lastActive: '2025-10-24 14:32:15',
  serviceProvider: {
    name: 'ServicePro UAE',
    apiBaseUrl: 'https://api.servicepro.ae/v2',
    // Outbound: AIS sends to Service Provider
    salesInvoiceEndpoint: '/invoices/sales',
    purchaseInvoiceStatusEndpoint: '/invoices/purchase/status',
    // Inbound: Service Provider sends to AIS
    receiveUrl: 'https://api.ais-erp.com/v2/CLT-001/receive-purchase',
    apiKey: 'sp_abc_key_secure_123',
    authType: 'Bearer Token',
    timeout: 30,
    retryAttempts: 3
  }
};

export default function ClientDetails({ clientId, onBack }: ClientDetailsProps) {
  const [client, setClient] = useState(mockClient);
  const [isEditing, setIsEditing] = useState(false);

  const handleSaveChanges = () => {
    setIsEditing(false);
    toast.success('Client details updated successfully');
  };

  const handleSuspendClient = () => {
    setClient({ ...client, status: client.status === 'active' ? 'suspended' : 'active' });
    toast.success(client.status === 'active' ? 'Client suspended' : 'Client activated');
  };

  const handleResetAPIKey = () => {
    toast.success('API key has been reset. New key sent to client email.');
  };

  const handleTestConnection = () => {
    toast.loading('Testing connection...');
    setTimeout(() => {
      toast.success('Connection successful! Service provider is responding.');
    }, 2000);
  };

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={onBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Clients
          </Button>
          <div>
            <h1 className="text-3xl text-gray-900">{client.name}</h1>
            <p className="text-gray-600 mt-1">{client.id}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <Badge className={client.status === 'active' ? 'bg-green-500' : 'bg-red-500'}>
            {client.status === 'active' ? 'Active' : 'Suspended'}
          </Badge>
          <Badge variant="outline">{client.subscription}</Badge>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button 
                variant={isEditing ? 'default' : 'outline'}
                onClick={() => isEditing ? handleSaveChanges() : setIsEditing(true)}
                className={isEditing ? 'bg-[#0052CC]' : ''}
              >
                <Save className="w-4 h-4 mr-2" />
                {isEditing ? 'Save Changes' : 'Edit Client'}
              </Button>
              <Button 
                variant="outline"
                onClick={handleSuspendClient}
              >
                <Ban className="w-4 h-4 mr-2" />
                {client.status === 'active' ? 'Suspend Client' : 'Activate Client'}
              </Button>
              <Button variant="outline" onClick={handleTestConnection}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Last Active</p>
              <p className="text-gray-900">{client.lastActive}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="trn-branches">TRN & Branches</TabsTrigger>
          <TabsTrigger value="service-provider">Service Provider</TabsTrigger>
          <TabsTrigger value="api">API Configuration</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Monthly Invoices</p>
                    <p className="text-2xl text-gray-900 mt-2">{client.monthlyInvoices.toLocaleString()}</p>
                  </div>
                  <TrendingUp className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">API Calls</p>
                    <p className="text-2xl text-gray-900 mt-2">{client.apiHits.toLocaleString()}</p>
                  </div>
                  <Activity className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Success Rate</p>
                    <p className="text-2xl text-green-600 mt-2">{client.successRate}%</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Failure Rate</p>
                    <p className="text-2xl text-red-600 mt-2">{client.failureRate}%</p>
                  </div>
                  <TrendingDown className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Company Name</Label>
                  <Input value={client.name} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input value={client.email} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={client.phone} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>Tax Registration Number (TRN)</Label>
                  <Input value={client.trn} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={client.address} disabled={!isEditing} className="mt-1" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Subscription Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Plan</Label>
                  <Input value={client.subscription} disabled className="mt-1" />
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input value={client.expiryDate} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>ERP System</Label>
                  <Input value={client.erp} disabled className="mt-1" />
                </div>
                <div>
                  <Label>ERP Version</Label>
                  <Input value={client.erpVersion} disabled={!isEditing} className="mt-1" />
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">
                    <Badge className={client.status === 'active' ? 'bg-green-500' : 'bg-red-500'}>
                      {client.status}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* TRN & Branches Tab */}
        <TabsContent value="trn-branches" className="space-y-6">
          <ClientTRNManagement clientId={clientId} clientName={client.name} />
        </TabsContent>

        {/* Service Provider Tab */}
        <TabsContent value="service-provider" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Service Provider Configuration</CardTitle>
                <Server className="w-5 h-5 text-gray-600" />
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Architecture Flow:</strong> Client ERP → AIS Portal → Service Provider → Government Authorities (FTA Peppol)
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  AIS does not directly communicate with FTA. The Service Provider acts as the intermediary for all government submissions.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <Label>Service Provider Name</Label>
                    <Input value={client.serviceProvider.name} disabled={!isEditing} className="mt-1" />
                  </div>
                  <div>
                    <Label>API Base URL</Label>
                    <Input value={client.serviceProvider.apiBaseUrl} disabled={!isEditing} className="mt-1" />
                  </div>
                  <div>
                    <Label>Authentication Type</Label>
                    <Input value={client.serviceProvider.authType} disabled={!isEditing} className="mt-1" />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Service Provider API Key</Label>
                    <Input type="password" value={client.serviceProvider.apiKey} disabled={!isEditing} className="mt-1" />
                  </div>
                  <div>
                    <Label>Timeout (seconds)</Label>
                    <Input type="number" value={client.serviceProvider.timeout} disabled={!isEditing} className="mt-1" />
                  </div>
                  <div>
                    <Label>Retry Attempts</Label>
                    <Input type="number" value={client.serviceProvider.retryAttempts} disabled={!isEditing} className="mt-1" />
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg text-gray-900 mb-4">Outbound API Endpoints (AIS → Service Provider)</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Sales Invoice Endpoint (POST)</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline" className="bg-green-50">POST</Badge>
                      <Input 
                        value={client.serviceProvider.apiBaseUrl + client.serviceProvider.salesInvoiceEndpoint} 
                        disabled={!isEditing} 
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">AIS sends sales invoices to service provider for submission to FTA</p>
                  </div>
                  
                  <div>
                    <Label>Purchase Invoice Status Endpoint (POST)</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline" className="bg-orange-50">POST</Badge>
                      <Input 
                        value={client.serviceProvider.apiBaseUrl + client.serviceProvider.purchaseInvoiceStatusEndpoint} 
                        disabled={!isEditing} 
                        className="flex-1"
                      />
                    </div>
                    <p className="text-xs text-gray-600 mt-1">AIS sends accept/reject status back to service provider for purchase invoices</p>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg text-gray-900 mb-4">Inbound API Endpoints (Service Provider → AIS)</h3>
                <div className="space-y-4">
                  <div>
                    <Label>Receive Purchase Invoice Endpoint</Label>
                    <div className="flex items-center space-x-2 mt-1">
                      <Badge variant="outline" className="bg-purple-50">POST</Badge>
                      <Input 
                        value={client.serviceProvider.receiveUrl} 
                        disabled 
                        className="flex-1"
                      />
                      <Button variant="outline" size="sm">Copy</Button>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Service provider forwards purchase invoices to this AIS endpoint</p>
                  </div>

                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <p className="text-sm text-gray-900 mb-2"><strong>Purchase Invoice Workflow:</strong></p>
                    <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
                      <li>Service Provider receives purchase invoice from vendor</li>
                      <li>Service Provider forwards to AIS via receive endpoint above</li>
                      <li>Client reviews and accepts/rejects in Purchase Invoices section</li>
                      <li>AIS sends status back to Service Provider via status endpoint</li>
                      <li>Service Provider processes based on accept/reject status</li>
                    </ol>
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="flex space-x-3">
                  <Button className="bg-[#0052CC]" onClick={handleSaveChanges}>
                    <Save className="w-4 h-4 mr-2" />
                    Save Service Provider Config
                  </Button>
                  <Button variant="outline" onClick={handleTestConnection}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Test Connection
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Configuration Tab */}
        <TabsContent value="api" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Client API Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Client ERP Endpoint URL</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <Input value={client.apiUrl} disabled={!isEditing} className="flex-1" />
                  <Button variant="outline" size="sm">Copy</Button>
                </div>
                <p className="text-xs text-gray-600 mt-1">The endpoint where client's ERP system sends invoice data</p>
              </div>

              <div>
                <Label>AIS Integration Endpoint (Client sends data here)</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <Input value={`https://api.ais-erp.com/v2/${client.id}/process`} disabled className="flex-1" />
                  <Button variant="outline" size="sm">Copy</Button>
                </div>
                <p className="text-xs text-gray-600 mt-1">This is the AIS endpoint that receives data from client's ERP</p>
              </div>

              <div>
                <Label>Client API Key</Label>
                <div className="flex items-center space-x-2 mt-1">
                  <Input type="password" value={client.apiKey} disabled className="flex-1" />
                  <Button variant="outline" size="sm">Show</Button>
                  <Button variant="outline" size="sm">Copy</Button>
                  <Button variant="outline" size="sm" onClick={handleResetAPIKey}>
                    <RefreshCw className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 pt-4">
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">Total API Calls</p>
                    <p className="text-2xl text-gray-900 mt-2">{client.apiHits.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">Successful</p>
                    <p className="text-2xl text-green-600 mt-2">
                      {Math.round(client.apiHits * client.successRate / 100).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <p className="text-sm text-gray-600">Failed</p>
                    <p className="text-2xl text-red-600 mt-2">
                      {Math.round(client.apiHits * client.failureRate / 100).toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Usage Trends (Last 4 Months)</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={usageData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="invoices" stroke="#0052CC" strokeWidth={2} name="Invoices" />
                  <Line yAxisId="right" type="monotone" dataKey="apiCalls" stroke="#36B37E" strokeWidth={2} name="API Calls" />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Invoice Type Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={invoiceTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(entry) => `${entry.type}: ${entry.count}`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {invoiceTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>API Success vs Failure</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={usageData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="success" fill="#36B37E" name="Success" />
                    <Bar dataKey="failed" fill="#FF5630" name="Failed" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Dangerous Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-yellow-300 bg-yellow-50 rounded-lg">
                <div>
                  <p className="text-gray-900">Suspend Client Account</p>
                  <p className="text-sm text-gray-600">Temporarily disable all API access and invoice processing</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-yellow-600 text-yellow-700 hover:bg-yellow-100"
                  onClick={handleSuspendClient}
                >
                  <Ban className="w-4 h-4 mr-2" />
                  {client.status === 'active' ? 'Suspend' : 'Activate'}
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 border border-red-300 bg-red-50 rounded-lg">
                <div>
                  <p className="text-gray-900">Reset API Key</p>
                  <p className="text-sm text-gray-600">Generate a new API key (current key will be invalidated)</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-red-600 text-red-700 hover:bg-red-100"
                  onClick={handleResetAPIKey}
                >
                  <AlertTriangle className="w-4 h-4 mr-2" />
                  Reset Key
                </Button>
              </div>

              <div className="flex items-center justify-between p-4 border border-blue-300 bg-blue-50 rounded-lg">
                <div>
                  <p className="text-gray-900">Test Service Provider Connection</p>
                  <p className="text-sm text-gray-600">Verify connectivity to the service provider API</p>
                </div>
                <Button 
                  variant="outline" 
                  className="border-blue-600 text-blue-700 hover:bg-blue-100"
                  onClick={handleTestConnection}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Test Now
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
